import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import type {
	ReportingEntry,
	ReportingEntryStatus,
} from "@/shared/cache/slices/reportingEntries";
import { useApi } from "@/shared/utils/request";

function toApiDate(d: Date): string {
	const y = d.getFullYear();
	const m = String(d.getMonth() + 1).padStart(2, "0");
	const day = String(d.getDate()).padStart(2, "0");
	return `${y}-${m}-${day}`;
}

// Fields the mapper reads. Other fields the backend returns (billable,
// entry_id, hourglass_feedback, modified_date, etc.) are dropped at this
// boundary — re-add here when a downstream consumer needs them.
interface AdminUserTransaction {
	id: number;
	prof_id: string;
	prof_name: string;
	date: string;
	start_time: string | null;
	end_time: string | null;
	units: number;
	original_units: number | null;
	description: string;
	original_description: string | null;
	matter_id: string | null;
	original_matter: string | null;
	matter_number: string | null;
	matter_description: string | null;
	client?: string | null;
	task_code: string | null;
	activity_code: string | null;
	original_task?: string | null;
	original_activity?: string | null;
	document_name: string | null;
	exported: boolean;
	approved: boolean;
	needs_improvement: boolean;
	attorney_feedback: string | null;
	app_name: string | null;
	units_seconds?: number | null;
	source: "transactions" | "deleted_transactions";
}

function statusOf(row: AdminUserTransaction): ReportingEntryStatus {
	if (row.source === "deleted_transactions") return "deleted";
	if (row.exported) return "exported";
	return "tracked";
}

function toReportingEntry(row: AdminUserTransaction): ReportingEntry {
	return {
		id: row.id,
		prof_id: row.prof_id,
		prof_name: row.prof_name,
		date: row.date,
		start_time: row.start_time,
		end_time: row.end_time,
		units: row.units,
		description: row.description,
		matter_id: row.matter_id,
		matter_number: row.matter_number,
		matter_description: row.matter_description,
		client: row.client ?? null,
		application: row.app_name,
		task: row.task_code,
		activity: row.activity_code,
		document_name: row.document_name,
		exported: row.exported,
		approved: row.approved,
		needs_improvement: row.needs_improvement,
		attorney_feedback: row.attorney_feedback,
		units_seconds: row.units_seconds ?? null,
		original_description: row.original_description,
		original_units: row.original_units,
		original_matter: row.original_matter,
		original_task: row.original_task ?? null,
		original_activity: row.original_activity ?? null,
		status: statusOf(row),
	};
}

// Profs are joined to a comma-string and Dates converted to YYYY-MM-DD before
// they reach the queryKey, so reference identity changes upstream don't
// trigger refetches — only value changes do.
export function useInternalEntries(
	profIds: string | string[] | null,
	startDate: Date,
	endDate: Date,
	includeDeleted = true,
) {
	const { makeApiRequest } = useApi();

	const profIdsKey = useMemo(() => {
		if (!profIds) return "";
		return Array.isArray(profIds) ? profIds.join(",") : profIds;
	}, [profIds]);

	const start = toApiDate(startDate);
	const end = toApiDate(endDate);

	const query = useQuery({
		queryKey: ["internal", "entries", profIdsKey, start, end, includeDeleted] as const,
		enabled: !!profIdsKey,
		queryFn: async ({ signal }) => {
			const params = new URLSearchParams({
				startDate: start,
				endDate: end,
				profIds: profIdsKey,
			});
			if (includeDeleted) params.set("includeDeleted", "true");
			const res = await makeApiRequest({
				endpoint: `/admin/user-transactions?${params.toString()}`,
				method: "GET",
				otherOptions: { signal },
			});
			if (res.error) throw new Error(String(res.error));
			return res.data as { transactions: AdminUserTransaction[] };
		},
	});

	const buckets = useMemo(() => {
		const out: Record<ReportingEntryStatus, ReportingEntry[]> = {
			tracked: [],
			exported: [],
			deleted: [],
		};
		for (const row of query.data?.transactions ?? []) {
			const entry = toReportingEntry(row);
			out[entry.status ?? "tracked"].push(entry);
		}
		return out;
	}, [query.data]);

	return {
		...buckets,
		isLoading: query.isLoading,
		isError: query.isError,
		error: query.error instanceof Error ? query.error.message : null,
	};
}

export function useInvalidateInternalEntries() {
	const queryClient = useQueryClient();
	return useCallback(
		(profId: string | null) => {
			if (!profId) return;
			queryClient.invalidateQueries({
				queryKey: ["internal", "entries", profId],
				exact: false,
			});
		},
		[queryClient],
	);
}
